import { InfoIcon } from 'lucide-react';

import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectSeparator,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { SavedSearch } from '@/lib/api/savedSearches';
import { cn } from '@/lib/utils';

interface SavedSearchSelectProps {
  savedSearches: SavedSearch[];
  value: string;
  onValueChange: (value: string) => void;
  hasActiveSavedSearch: boolean;
  className?: string;
}

export function SavedSearchSelect({
  savedSearches,
  value,
  onValueChange,
  hasActiveSavedSearch,
  className,
}: SavedSearchSelectProps) {
  return (
    <div className={cn('flex flex-col gap-1 sm:w-72', className)}>
      <div className="flex items-center gap-2">
        <Label
          htmlFor="saved-search-select"
          className="text-sm font-medium text-neutral-700">
          Căutări salvate
        </Label>
        <TooltipProvider delayDuration={200}>
          <Tooltip>
            <TooltipTrigger asChild>
              <button
                type="button"
                aria-label="Informații despre sincronizarea căutărilor salvate"
                className="rounded-sm text-neutral-400 transition hover:text-neutral-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-neutral-300">
                <InfoIcon className="h-4 w-4" />
              </button>
            </TooltipTrigger>
            <TooltipContent className="max-w-[240px] text-xs leading-snug">
              Pentru sincronizare între dispozitive, creează un cont. Fără cont,
              căutările sunt salvate doar pe acest dispozitiv.
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      </div>
      <Select value={value} onValueChange={onValueChange}>
        <SelectTrigger
          id="saved-search-select"
          className={cn(
            'w-full border-neutral-300 bg-white text-neutral-700 sm:w-72',
            hasActiveSavedSearch && 'border-neutral-800 text-neutral-900'
          )}>
          <SelectValue placeholder="Selectează o căutare" />
        </SelectTrigger>
        <SelectContent>
          {savedSearches.length > 0 ? (
            <>
              <SelectItem value="__clear__">Nicio căutare activă</SelectItem>
              <SelectSeparator />
              {savedSearches.map((search) => (
                <SelectItem key={search.id} value={String(search.id)}>
                  {search.name}
                </SelectItem>
              ))}
            </>
          ) : (
            <SelectItem value="__empty__" disabled>
              Nu există căutări salvate încă
            </SelectItem>
          )}
        </SelectContent>
      </Select>
    </div>
  );
}
